import type { StateAbbreviation } from "@nimble/data-service-client/types";
import {
  type GradeKey,
  sortGradesByOrder,
} from "@nimble/datamodel/enums/connectGrades";
import { Zap } from "lucide-react";
import { UseFormReturn } from "react-hook-form";

import { FilterValues } from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/filter-schema";
import SavedSchoolsFilter from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/saved-schools-filter";
import DistrictSelect from "@/components/shared/district/district-select";
import FilterSection from "@/components/shared/filters/filter-section";
import GradesFilterView from "@/components/shared/filters/grades-filter.view";
import MilesSlider from "@/components/shared/input/miles-slider";
import LocationDropdownStateCheck from "@/components/shared/location-dropdown/location-dropdown-state-check/location-dropdown-state-check";
import SchoolSelect from "@/components/shared/school/school-select";
import { AllSubjectSelect } from "@/components/shared/subject/subject-select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import useRecentSearches from "@/hooks/use-recent-searches";
import useStateConfig from "@/hooks/use-state-config";
import { DEFAULT_MILE_RANGE, JOB_SEARCH_MILES_RANGES } from "@/lib/constants";
import { jobFiltersTestIds } from "@/lib/data-test-ids/jobs.data-test-ids";
import { isIndividualStateConfig, type StateConfig } from "@/lib/states-config";

function getStateAbbreviation(
  stateConfig: StateConfig | null,
): StateAbbreviation | null {
  return stateConfig && isIndividualStateConfig(stateConfig)
    ? stateConfig.abbreviation
    : null;
}

function toSelection(value: string[] | undefined): string[] {
  if (!value) {
    return [];
  }

  return value;
}

function toGradeSelection(value: string[] | undefined): GradeKey[] {
  return sortGradesByOrder(toSelection(value));
}

const SchoolsFilter = ({
  form,
  stateConfig,
}: {
  form: UseFormReturn<FilterValues>;
  stateConfig: StateConfig | null;
}) => {
  return (
    <FormField
      control={form.control}
      name="schools"
      render={({ field }) => (
        <FormItem>
          <FormControl>
            <SchoolSelect
              value={toSelection(field.value)}
              onChange={field.onChange}
              inclusiveLabel={true}
              title="School"
              state={getStateAbbreviation(stateConfig)}
              data-testid={jobFiltersTestIds.SCHOOL_FILTER}
            />
          </FormControl>
        </FormItem>
      )}
    />
  );
};

const DistrictsFilter = ({
  form,
  stateConfig,
}: {
  form: UseFormReturn<FilterValues>;
  stateConfig: StateConfig | null;
}) => {
  return (
    <FormField
      control={form.control}
      name="districts"
      render={({ field }) => {
        return (
          <FormItem>
            <FormControl>
              <DistrictSelect
                value={field.value}
                onChange={field.onChange}
                inclusiveLabel={true}
                title="District"
                state={getStateAbbreviation(stateConfig)}
                data-testid={jobFiltersTestIds.DISTRICT_FILTER}
              />
            </FormControl>
          </FormItem>
        );
      }}
    />
  );
};

const AddressFilter = ({
  form,
  stateConfig,
}: {
  form: UseFormReturn<FilterValues>;
  stateConfig: StateConfig;
}) => {
  const recentSearches = useRecentSearches((s) => s.recentSearches);

  return (
    <>
      <FormField
        control={form.control}
        name="address"
        render={({ field }) => (
          <FormItem className="w-full">
            <FormControl>
              <LocationDropdownStateCheck
                value={field.value}
                onChange={field.onChange}
                currentState={stateConfig}
                topCities={stateConfig.topCities}
                topCitiesLabel={
                  stateConfig.searchInLabel
                    ? `Popular in ${stateConfig.searchInLabel}`
                    : undefined
                }
                recentSearches={recentSearches}
                placeholder={
                  stateConfig.searchInLabel
                    ? `Search in ${stateConfig.searchInLabel}`
                    : "Search location"
                }
                dataTestId={jobFiltersTestIds.LOCATION_FILTER}
                useStrictAddress={false}
              />
            </FormControl>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="radius"
        render={({ field }) => (
          <FormItem>
            <FormLabel
              htmlFor="commute-distance"
              className="text-xs font-medium text-ink-emphasis"
            >
              Max Distance
            </FormLabel>
            <FormControl>
              <div className="px-2">
                <MilesSlider
                  value={field.value ?? DEFAULT_MILE_RANGE}
                  onChange={field.onChange}
                  showValue={false}
                  milesRanges={JOB_SEARCH_MILES_RANGES}
                  className="[&_[data-slot=slider-range]]:bg-ink-subtle [&_[data-slot=slider-thumb]]:border-ink-subtle [&_[data-slot=slider-thumb]]:ring-ink-subtle/50"
                />
              </div>
            </FormControl>
          </FormItem>
        )}
      />
    </>
  );
};

const GradesFilter = ({ form }: { form: UseFormReturn<FilterValues> }) => {
  return (
    <FormField
      control={form.control}
      name="grades"
      render={({ field }) => (
        <FormItem>
          <GradesFilterView
            value={toGradeSelection(field.value)}
            onChange={field.onChange}
          />
        </FormItem>
      )}
    />
  );
};

const SubjectsFilter = ({ form }: { form: UseFormReturn<FilterValues> }) => {
  return (
    <FormField
      control={form.control}
      name="subjects"
      render={({ field }) => (
        <FormItem>
          <FormControl>
            <AllSubjectSelect
              value={toSelection(field.value)}
              onChange={field.onChange}
              inclusiveLabel={true}
              title="Subject Area"
              data-testid={jobFiltersTestIds.SUBJECT_FILTER}
            />
          </FormControl>
        </FormItem>
      )}
    />
  );
};

const ShowSchoolsWithNoRolesFilter = ({
  form,
}: {
  form: UseFormReturn<FilterValues>;
}) => {
  return (
    <FormField
      control={form.control}
      name="showSchoolsWithNoRoles"
      render={({ field }) => (
        <FormItem>
          <div className="flex items-center justify-between mb-1">
            <FormLabel
              htmlFor="show-no-roles"
              className="text-sm font-medium text-ink-emphasis cursor-pointer"
            >
              Show schools without open roles
            </FormLabel>
            <FormControl>
              <Switch
                id="show-no-roles"
                data-testid={jobFiltersTestIds.SHOW_SCHOOLS_NO_ROLES}
                checked={field.value || false}
                onCheckedChange={field.onChange}
                aria-label="Show schools without open roles"
              />
            </FormControl>
          </div>
        </FormItem>
      )}
    />
  );
};

const QuickApplyFilter = ({ form }: { form: UseFormReturn<FilterValues> }) => {
  return (
    <FormField
      control={form.control}
      name="quickApply"
      render={({ field }) => (
        <FormItem>
          <div className="flex items-center justify-between mb-1">
            <FormLabel
              htmlFor="quick-apply"
              className="text-sm font-medium text-brand-purple cursor-pointer flex items-center gap-2"
            >
              <Zap className="h-4 w-4 text-brand-purple" />
              Quick Apply
            </FormLabel>
            <FormControl>
              <Switch
                id="quick-apply"
                checked={field.value || false}
                onCheckedChange={field.onChange}
                aria-label="Quick Apply"
                data-testid={jobFiltersTestIds.QUICK_APPLY_FILTER}
              />
            </FormControl>
          </div>
        </FormItem>
      )}
    />
  );
};

export default function SearchFiltersView({
  form,
}: {
  form: UseFormReturn<FilterValues>;
}) {
  const stateConfig = useStateConfig();

  return (
    <Form {...form}>
      <form className="space-y-4 [&_[role=combobox]]:shadow-none [&_[role=combobox]]:border-border [&_[role=combobox]]:text-muted-foreground">
        <FilterSection title="Location & Distance">
          <AddressFilter form={form} stateConfig={stateConfig} />
          <ShowSchoolsWithNoRolesFilter form={form} />
          <QuickApplyFilter form={form} />
          <SavedSchoolsFilter form={form} />
        </FilterSection>
        <FilterSection title="District & School">
          <DistrictsFilter form={form} stateConfig={stateConfig} />
          <SchoolsFilter form={form} stateConfig={stateConfig} />
          <SubjectsFilter form={form} />
          <GradesFilter form={form} />
        </FilterSection>
      </form>
    </Form>
  );
}
